import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Upload, Save, FileText, AlertCircle, CheckCircle2, Zap } from "lucide-react";
import { api } from "../../lib/api";

/** Edit the raw source text a chapter's LLM pipeline reads.
 *
 * Three ways to update it:
 *  1. Paste / type in the textarea, click Save.
 *  2. Upload a PDF — server extracts text with pypdf.
 *  3. Upload a TXT file.
 *
 * After saving, admin can click "Regenerate this chapter" which kicks off
 * the normal generation pipeline (EN + AR, all kinds) so students see the
 * refreshed content right away.
 */
export default function ChapterSourceModal({ chapterId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ tone: null, msg: "" });
  const [mode, setMode] = useState("replace");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/chapters/${chapterId}/source`);
      setData(r.data);
      setText(r.data.text || "");
    } finally { setLoading(false); }
  }, [chapterId]);

  useEffect(() => { load(); }, [load]);

  // Poll active regeneration job.
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/admin/generate/${jobId}`);
        setJob(r.data);
        if (r.data.status === "running" || r.data.status === "queued") {
          setTimeout(tick, 3000);
        }
      } catch (err) {
        // Stop polling on fetch failure — the modal will simply show the
        // last known state until the user reopens it. Log so the bug is
        // discoverable in the browser console rather than silently swallowed.
        console.warn("Generation job poll failed:", err?.response?.data?.detail || err?.message || err);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [jobId]);

  const save = async () => {
    setSaving(true);
    setStatus({ tone: null, msg: "" });
    try {
      const r = await api.put(`/admin/chapters/${chapterId}/source`, { text });
      setStatus({ tone: "ok", msg: `Saved · ${r.data.length.toLocaleString()} characters` });
      await load();
    } catch (e) {
      setStatus({ tone: "err", msg: e?.response?.data?.detail || "Couldn't save" });
    } finally { setSaving(false); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setStatus({ tone: null, msg: "" });
    setUploadProgress(10);
    const form = new FormData();
    form.append("file", file);
    form.append("mode", mode);
    try {
      const r = await api.post(
        `/admin/chapters/${chapterId}/source/upload`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => setUploadProgress(Math.round((evt.loaded / (evt.total || 1)) * 60) + 20),
        },
      );
      setUploadProgress(100);
      setStatus({
        tone: "ok",
        msg: `Extracted ${r.data.extracted_chars.toLocaleString()} chars from ${file.name} · total now ${r.data.total_chars.toLocaleString()} chars`,
      });
      await load();
    } catch (e) {
      setStatus({ tone: "err", msg: e?.response?.data?.detail || "Upload failed" });
    } finally {
      setTimeout(() => setUploadProgress(0), 1500);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const regenerate = async () => {
    if (!window.confirm(`Regenerate every kind (lesson, flashcards, quiz, scenario, summary) for chapter ${data?.chapter_number} in both English and Arabic? This consumes LLM budget.`)) return;
    setStatus({ tone: null, msg: "" });
    try {
      const r = await api.post("/admin/generate", {
        chapter_ids: [chapterId],
        include_arabic: true,
        overwrite: true,
      });
      setJobId(r.data.job_id);
      setJob({ status: "queued", done: 0, total: 0 });
    } catch (e) {
      setStatus({ tone: "err", msg: e?.response?.data?.detail || "Couldn't start generation" });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="chapter-source-modal">
      <div className="absolute inset-0 bg-[#1a1a1a]/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ms-auto w-full max-w-3xl bg-bone border-s border-border overflow-y-auto h-full">
        <header className="sticky top-0 bg-bone/95 backdrop-blur border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Source text · Chapter {data?.chapter_number ?? "…"}</div>
            <div className="font-display text-base font-semibold truncate">{data?.chapter_title || "Loading…"}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary" data-testid="chapter-source-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </header>

        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="text-xs text-muted-foreground leading-relaxed">
              This is the raw text the LLM reads to build the lesson, flashcards, quiz, scenario and summary. Paste content directly, or upload a PDF / TXT file. Up to ~22,000 chars are passed to Claude per chapter.
            </div>

            {/* Upload row */}
            <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-sage" />
                <div className="font-medium text-sm">Upload PDF or TXT</div>
              </div>
              <div className="grid sm:grid-cols-[auto_1fr] gap-3 items-center">
                <label className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer" data-testid="chapter-source-upload-btn">
                  <Upload className="w-4 h-4" /> Choose file
                  <input
                    type="file"
                    ref={fileRef}
                    accept=".pdf,.txt,application/pdf,text/plain"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="chapter-source-file-input"
                  />
                </label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-bone text-sm focus:border-sage focus:outline-none"
                  data-testid="chapter-source-mode"
                >
                  <option value="replace">Replace existing text</option>
                  <option value="append">Append to existing text</option>
                </select>
              </div>
              {uploadProgress > 0 && (
                <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-sage transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">
                Scanned-image PDFs won't work — you'll get an error telling you to paste the text instead.
              </div>
            </div>

            {/* Textarea */}
            <div className="bg-white border border-border rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-sage" />
                  <div className="font-medium text-sm">Source text</div>
                </div>
                <div className="text-xs text-muted-foreground" data-testid="chapter-source-char-count">
                  {text.length.toLocaleString()} chars
                </div>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste or type the chapter source here…"
                rows={18}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-bone focus:outline-none focus:border-sage font-mono text-xs leading-relaxed resize-y"
                data-testid="chapter-source-textarea"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={save}
                  disabled={saving || text === (data?.text || "")}
                  className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg"
                  data-testid="chapter-source-save"
                >
                  <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save source"}
                </button>
                <button
                  onClick={regenerate}
                  disabled={!text || job?.status === "running" || job?.status === "queued"}
                  className="inline-flex items-center gap-2 bg-white border border-border hover:bg-secondary disabled:opacity-40 text-sm font-medium px-4 py-2 rounded-lg"
                  data-testid="chapter-source-regenerate"
                >
                  <Zap className="w-4 h-4" /> Regenerate this chapter
                </button>
                {data?.updated_at && (
                  <div className="text-[11px] text-muted-foreground ms-auto">
                    Last updated: {new Date(data.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {status.msg && (
              <div
                className={`p-3 rounded-lg text-sm flex items-start gap-2 ${
                  status.tone === "ok"
                    ? "bg-sage/10 border border-sage/30 text-sage"
                    : "bg-terracotta/10 border border-terracotta/30 text-terracotta"
                }`}
                data-testid="chapter-source-status"
              >
                {status.tone === "ok" ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                <div>{status.msg}</div>
              </div>
            )}

            {job && (
              <div className="bg-white border border-border rounded-2xl p-4" data-testid="chapter-source-job">
                <div className="flex items-center justify-between mb-2 text-sm">
                  <div className="font-medium">Regeneration · <span className="text-muted-foreground">{job.status}</span></div>
                  <div className="font-mono">{job.done}/{job.total}</div>
                </div>
                <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-sage transition-all" style={{ width: job.total ? `${(job.done / job.total) * 100}%` : "0%" }} />
                </div>
                {job.last_error && <div className="mt-2 text-xs text-terracotta">{job.last_error}</div>}
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
